/**
 * Consent purposes and the policy version they are recorded against
 * (contract A8/A25, PRD §9.1.1, DPIA purposes).
 *
 * The purposes are the DPIA purpose ids as slugs. Service delivery
 * (P1) rides on the contract basis and has no consent row at all —
 * registration completes without one, and nothing here is a condition of
 * registering. Only the two Art. 6(1)(a) purposes appear:
 *
 * - `product_analytics` (P6) — Statsig and all product analytics.
 * - `reminder_email` (P7) — reminder and progress email.
 *
 * Both default OFF. "Default off" is not a UI default here: it is the
 * absence of a live row, so a user who has never answered and a user who
 * has withdrawn are the same state, and no client default can accidentally
 * grant anything.
 *
 * `POLICY_VERSION` mirrors `POLICY_VERSION` in
 * `api/src/revisionlab_api/services/consent_service.py` (a date naming the
 * consent wording in force). It is a baked
 * constant rather than something fetched at runtime *on purpose*: the
 * version must belong to the same build that renders the wording, or the
 * client could show 2026-09-07 text while recording consent against a
 * version the server bumped mid-session. `policy.test.ts` parses the
 * Python constant and fails if the two ever diverge.
 *
 * The server rejects a `policy_version` that is not its current constant
 * with 409 `policy_version_stale`, which is exactly the stale-build case:
 * the fix is to reload into the build carrying the new wording, not to
 * retry the same body.
 */

/** DPIA P6 — Statsig and all product analytics. Art. 6(1)(a). */
export const PRODUCT_ANALYTICS = "product_analytics";

/** DPIA P7 — reminder and progress email. Art. 6(1)(a). */
export const REMINDER_EMAIL = "reminder_email";

export const CONSENT_PURPOSES = [PRODUCT_ANALYTICS, REMINDER_EMAIL] as const;

export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number];

/** Mirrors `consent_service.POLICY_VERSION`; pinned by `policy.test.ts`. */
export const POLICY_VERSION = "2026-09-07";

/**
 * What each toggle promises, in the user's words. Held next to the version
 * it belongs to: the wording and the `policy_version` recorded against it
 * are the same artefact, and a reader changing one must see the other.
 */
export const CONSENT_COPY: Record<
  ConsentPurpose,
  { readonly label: string; readonly detail: string }
> = {
  [PRODUCT_ANALYTICS]: {
    label: "Product analytics",
    detail:
      "Lets us see which features are used, so we can improve them. Event names from a fixed list plus non-identifying details — never your name, email, answers, questions or grades.",
  },
  [REMINDER_EMAIL]: {
    label: "Reminder and progress email",
    detail:
      "Occasional email nudging you back to revision and summarising your progress. Nothing about pricing or offers.",
  },
};
